import { useEffect } from 'react';
import {
  App as AntApp,
  Button,
  Card,
  Form,
  Input,
  InputNumber,
  Skeleton,
  Switch,
  Typography,
} from 'antd';
import { useTranslation } from 'react-i18next';

import { ErrorAlert } from '@/components/ErrorAlert';
import { usePricingQuery, useUpdatePriceMutation, type PriceApp, type PriceItem } from './hooks';

const { Title, Text } = Typography;

interface PriceFormValues {
  amount_kzt: number;
  enabled: boolean;
  payment_note: string;
}

// Одна строка тарифа (V1.4): цена в ₸ (целое ≥ 0), тумблер «услуга включена»
// (выключенная не отдаётся публично и не принимает заявок), реквизиты оплаты
// для письма заявителю. PATCH /platform/pricing/{app}/{code}; пустые
// реквизиты уходят как null. Название услуги — словарь по коду, неизвестный
// код показываем как есть.
function PriceItemForm({ item }: { item: PriceItem }): JSX.Element {
  const { t } = useTranslation();
  const { message } = AntApp.useApp();
  const [form] = Form.useForm<PriceFormValues>();
  const mutation = useUpdatePriceMutation();

  useEffect(() => {
    form.setFieldsValue({
      amount_kzt: item.amount_kzt,
      enabled: item.enabled,
      payment_note: item.payment_note ?? '',
    });
  }, [item, form]);

  const onFinish = (values: PriceFormValues): void => {
    mutation.mutate(
      {
        app: item.app,
        code: item.code,
        amount_kzt: Math.round(values.amount_kzt),
        enabled: values.enabled,
        payment_note: values.payment_note.trim() || null,
      },
      { onSuccess: () => void message.success(t('platform.pricing.saved')) },
    );
  };

  const name = t(`platform.pricing.services.${item.code}`, { defaultValue: item.code });
  const unit = t(`platform.pricing.units.${item.unit}`, { defaultValue: item.unit });

  return (
    <Form<PriceFormValues>
      form={form}
      layout="vertical"
      onFinish={onFinish}
      data-testid={`pricing-form-${item.app}-${item.code}`}
    >
      <Title level={5} style={{ marginTop: 0 }}>
        {name}
      </Title>
      <Form.Item
        label={t('platform.pricing.amount', { unit })}
        name="amount_kzt"
        rules={[
          { required: true, message: t('platform.pricing.amount_required') },
          { type: 'integer', min: 0, message: t('platform.pricing.amount_invalid') },
        ]}
      >
        <InputNumber
          min={0}
          precision={0}
          step={500}
          addonAfter="₸"
          style={{ width: 220 }}
          data-testid={`pricing-amount-${item.code}`}
        />
      </Form.Item>
      <Form.Item
        label={t('platform.pricing.enabled')}
        name="enabled"
        valuePropName="checked"
        extra={t('platform.pricing.enabled_hint')}
      >
        <Switch data-testid={`pricing-enabled-${item.code}`} />
      </Form.Item>
      <Form.Item
        label={t('platform.pricing.payment_note')}
        name="payment_note"
        extra={t('platform.pricing.payment_note_hint')}
      >
        <Input.TextArea
          rows={3}
          maxLength={1000}
          showCount
          data-testid={`pricing-note-${item.code}`}
        />
      </Form.Item>
      {mutation.error && (
        <div style={{ marginBottom: 16 }}>
          <ErrorAlert error={mutation.error} />
        </div>
      )}
      <Button
        type="primary"
        htmlType="submit"
        loading={mutation.isPending}
        data-testid={`pricing-save-${item.app}-${item.code}`}
      >
        {t('common.save')}
      </Button>
    </Form>
  );
}

const PRICE_APPS: readonly PriceApp[] = ['light', 'vision'];

// Карточка «Платные услуги» одного приложения (стиль AppSettingsCard):
// по форме на каждую услугу; у Light услуг пока нет — подпись.
function PricingCard({ app, items }: { app: PriceApp; items: PriceItem[] }): JSX.Element {
  const { t } = useTranslation();
  return (
    <Card
      title={t(`platform.settings.apps.name.${app}`)}
      variant="outlined"
      style={{ marginTop: 16 }}
      data-testid={`pricing-card-${app}`}
    >
      {items.length === 0 ? (
        <Text type="secondary">{t('platform.pricing.none')}</Text>
      ) : (
        items.map((item) => <PriceItemForm key={item.code} item={item} />)
      )}
    </Card>
  );
}

// Страница «Платные услуги» (V1.4). Изначально блок жил в «Настройках»
// платформы; вынесен в основное меню решением пользователя 2026-09-11:
// цены — бизнес-логика и должны быть на виду, в «Настройках» остаются
// технические детали. GET /platform/pricing, карточки по приложениям.
export function PricingPage(): JSX.Element {
  const { t } = useTranslation();
  const pricing = usePricingQuery();

  return (
    <div style={{ maxWidth: 680 }}>
      <Title level={3} style={{ marginBottom: 4 }}>
        {t('platform.pricing.title')}
      </Title>
      <Text type="secondary">{t('platform.pricing.description')}</Text>
      {pricing.error && (
        <div style={{ marginTop: 16 }}>
          <ErrorAlert error={pricing.error} />
        </div>
      )}
      {pricing.isLoading && <Skeleton active style={{ marginTop: 16 }} />}
      {pricing.data &&
        PRICE_APPS.map((app) => (
          <PricingCard key={app} app={app} items={pricing.data.filter((p) => p.app === app)} />
        ))}
    </div>
  );
}
